import ServiceLayout from "@/components/ServiceLayout";
import { QuoteModal } from "@/components/QuoteModal";
import { Button } from "@/components/ui/button";
import { Phone } from "lucide-react";

export default function Augsburg() {
  return (
    <ServiceLayout
      title="Umzugsunternehmen in Augsburg"
      description="Ihr lokaler Partner für stressfreie Umzüge, Möbelmontage und Handwerksleistungen direkt in Augsburg und Umgebung."
      image="https://vibe.filesafe.space/1781514922662573702/assets/c2775eb8-1015-4c7f-99d2-e612975703a2.png"
      heroBg="https://vibe.filesafe.space/1781514922662573702/assets/13e3feee-77f2-4071-8835-0b44ed004a4c.png"
      content={
        <>
          <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight">
            Ihr Umzug in Augsburg
          </h2>
          <p className="text-lg text-neutral-600">
            Als regional verwurzeltes Umzugsunternehmen kennen wir Augsburg und die umliegenden Stadtteile wie unsere Westentasche. Egal ob Sie innerhalb der Fuggerstadt umziehen, neu zuziehen oder Augsburg verlassen – wir sorgen für einen reibungslosen Ablauf.
          </p>
          <p className="text-lg text-neutral-600">
            Von der Halteverbotszone in der engen Altstadt bis zum reibungslosen Transport durchs Univiertel: Wir planen präzise und setzen Ihren Umzug termingerecht um.
          </p>
          <ul className="space-y-3 mt-6">
            {[
              "Ortskundige Fahrer & Planer",
              "Einrichtung von Halteverbotszonen",
              "Kurze Anfahrtswege",
              "Kostenlose Besichtigung vor Ort"
            ].map((item, i) => (
              <li key={i} className="flex items-center gap-3 text-neutral-800 font-medium">
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                </div>
                {item}
              </li>
            ))}
          </ul>
        </>
      }
      extendedContent={
        <div className="bg-white">
          <section className="py-16 bg-neutral-50">
            <div className="container mx-auto px-4 md:px-6">
              <div className="max-w-4xl mx-auto space-y-8">
                <div>
                  <h2 className="font-heading text-3xl font-bold mb-4">Professionelles Umzugsunternehmen in Augsburg – Stressfrei und sicher umziehen</h2>
                  <p className="text-neutral-600 leading-relaxed mb-4">
                    Ein Umzug, ob privat oder gewerblich, ist oft mit viel Stress, Zeitaufwand und organisatorischen Herausforderungen verbunden. Genau hier kommen wir ins Spiel: Als Ihr erfahrenes Umzugsunternehmen in Augsburg nehmen wir Ihnen die schwere Arbeit ab. Mit jahrelanger Erfahrung, einem bestens geschulten Team und einer modernen Fahrzeugflotte sorgen wir dafür, dass Ihr Hab und Gut sicher, pünktlich und unbeschadet an Ihrem neuen Wohn- oder Firmenort ankommt.
                  </p>
                  <p className="text-neutral-600 leading-relaxed mb-4">
                    Die Fuggerstadt Augsburg mit ihren historischen Gassen, engen Altstadtstraßen und belebten Vierteln wie Pfersee, Lechhausen oder Göggingen erfordert beim Umzug besondere Ortskenntnis. Wir kennen die verkehrstechnischen Tücken der Stadt und planen Ihren Umzug bis ins kleinste Detail. Von der rechtzeitigen Beantragung und Einrichtung von Halteverbotszonen bis hin zur Wahl der optimalen Route – wir überlassen nichts dem Zufall.
                  </p>
                  <div className="pt-4 pb-2 flex flex-col sm:flex-row items-start gap-4">
                    <QuoteModal>
                      <Button size="lg" className="font-bold text-base px-8 py-6 rounded-none bg-primary text-white hover:bg-primary/90 w-full sm:w-auto">
                        Kostenloses Angebot anfordern
                      </Button>
                    </QuoteModal>
                    <Button size="lg" variant="secondary" asChild className="w-full sm:w-auto">
                      <a href="tel:+4915888640762">
                        <Phone className="w-5 h-5 mr-2" />
                        +49 15888 640762
                      </a>
                    </Button>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-8 pt-8">
                  <div className="bg-white p-8 border border-neutral-200">
                    <h3 className="font-heading text-xl font-bold mb-3">Privatumzüge in Augsburg</h3>
                    <p className="text-neutral-600 leading-relaxed">
                      Egal ob Sie in eine kleine Wohnung im Univiertel oder in ein großes Einfamilienhaus in Haunstetten ziehen – wir behandeln jeden Privatumzug mit der gleichen Sorgfalt. Wir bieten Ihnen maßgeschneiderte Lösungen, vom reinen Transport bis hin zum Full-Service-Umzug inklusive Ein- und Auspackservice.
                    </p>
                  </div>
                  <div className="bg-white p-8 border border-neutral-200">
                    <h3 className="font-heading text-xl font-bold mb-3">Firmenumzüge & Büroumzüge</h3>
                    <p className="text-neutral-600 leading-relaxed">
                      Zeit ist Geld. Bei einem Firmenumzug in Augsburg sorgen wir dafür, dass Ihre Ausfallzeiten auf ein absolutes Minimum reduziert werden. Wir transportieren IT-Equipment, schwere Akten und empfindliche Büromöbel sicher und bauen Arbeitsplätze am neuen Standort schnell wieder auf.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-16 bg-white">
            <div className="container mx-auto px-4 md:px-6">
              <div className="max-w-4xl mx-auto space-y-8">
                <div>
                  <h2 className="font-heading text-3xl font-bold mb-4">Mehr als nur Transport: Unsere Zusatzleistungen für Augsburg</h2>
                  <p className="text-neutral-600 leading-relaxed mb-4">
                    Ein moderner Umzugsservice endet nicht beim Tragen von Kartons. Um Ihnen den Neustart in Augsburg so angenehm wie möglich zu machen, bieten wir ein breites Spektrum an Handwerks- und Zusatzleistungen an. Unsere erfahrenen Monteure übernehmen die fachgerechte Demontage und Montage Ihrer Möbel. Auch komplexe Systeme wie Küchen, Schwebetürenschränke oder Boxspringbetten sind für uns kein Problem.
                  </p>
                  <p className="text-neutral-600 leading-relaxed mb-4">
                    Darüber hinaus kümmern wir uns um die Elektro- und Lichtinstallation in Ihrem neuen Zuhause. Wir schließen Herde an, montieren Deckenlampen und sorgen dafür, dass Waschmaschine und Spülmaschine sofort einsatzbereit sind. Wenn in der alten Wohnung noch Arbeiten anstehen, übernehmen wir auch Bodenbelagsarbeiten oder kleinere Renovierungen, damit Sie die Immobilie besenrein und vertragsgerecht an den Vermieter übergeben können.
                  </p>
                  <div className="pt-4 pb-2 flex flex-col sm:flex-row items-start gap-4">
                    <QuoteModal>
                      <Button size="lg" className="font-bold text-base px-8 py-6 rounded-none bg-primary text-white hover:bg-primary/90 w-full sm:w-auto">
                        Jetzt beraten lassen
                      </Button>
                    </QuoteModal>
                  </div>
                </div>

                <div className="bg-primary/5 p-8 border-l-4 border-primary mt-8">
                  <h3 className="font-heading text-2xl font-bold mb-3">Transparenz und Sicherheit beim Umzug</h3>
                  <p className="text-neutral-700 leading-relaxed">
                    Vertrauen ist die Basis unserer Arbeit. Deshalb bieten wir Ihnen für Ihren Umzug in Augsburg absolute Preistransparenz. Nach einer kostenlosen und unverbindlichen Vor-Ort-Besichtigung erhalten Sie von uns ein Festpreisangebot. Keine versteckten Kosten, keine bösen Überraschungen am Umzugstag. Zudem sind wir vollumfänglich versichert – sollte doch einmal etwas zu Bruch gehen, sind Sie finanziell auf der sicheren Seite.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="py-16 bg-neutral-950 text-white">
            <div className="container mx-auto px-4 md:px-6">
              <div className="max-w-4xl mx-auto text-center">
                <h2 className="font-heading text-3xl font-bold mb-6">Warum Easy Umzug in Augsburg?</h2>
                <div className="grid sm:grid-cols-3 gap-8 mt-12 text-left">
                  <div>
                    <div className="w-12 h-12 bg-primary flex items-center justify-center mb-4">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>
                    </div>
                    <h3 className="font-bold text-lg mb-2">Lokale Expertise</h3>
                    <p className="text-neutral-400">Wir kennen die Straßen, Verkehrsregeln und Parkplatzsituationen in allen Augsburger Stadtteilen genau.</p>
                  </div>
                  <div>
                    <div className="w-12 h-12 bg-primary flex items-center justify-center mb-4">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                    </div>
                    <h3 className="font-bold text-lg mb-2">Pünktlichkeit & Zuverlässigkeit</h3>
                    <p className="text-neutral-400">Auf unsere Terminabsprachen können Sie sich zu 100% verlassen. Wir arbeiten schnell und effizient.</p>
                  </div>
                  <div>
                    <div className="w-12 h-12 bg-primary flex items-center justify-center mb-4">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path></svg>
                    </div>
                    <h3 className="font-bold text-lg mb-2">Alles aus einer Hand</h3>
                    <p className="text-neutral-400">Vom Transport über die Montage bis hin zu weiteren Handwerksleistungen bieten wir Ihnen einen echten Komplettservice.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      }
    />
  );
}
